import { outcomeAmountRefusal, OUTCOME_AMOUNT_LABELS } from "./outcomeTag";
import { priceBandOf, type PriceBand } from "./priceBand";

/**
 * B2 (revenue build list, ruling 4): what an outcome says about money, and what a thread's outcomes add up to.
 * Pure. The cents are integers from the money module (F-16); nothing here parses or does model arithmetic.
 *
 * "Unknown" is a recorded answer, never a default and never zero. A sold or deposit tap carries a stated
 * amount or the owner's explicit "amount not known"; neither is refused (CLAUDE.md section 0).
 */
export const OUTCOME_AMOUNT_STATUSES = ["stated", "unknown"] as const;
export type OutcomeAmountStatus = (typeof OUTCOME_AMOUNT_STATUSES)[number];

export type OutcomeMoney =
  | {
      readonly ok: true;
      readonly amountCents: number | null;
      readonly amountStatus: OutcomeAmountStatus | null;
      readonly priceBand: PriceBand | null;
    }
  | { readonly ok: false; readonly refusal: string };

const NEITHER_REFUSAL = (label: string): string =>
  `A ${label} outcome needs an amount or the choice "amount not known". Neither was given, and a silent blank is not recorded.`;
const BOTH_REFUSAL = "An amount and \"amount not known\" were both given. Record one or the other.";
const UNKNOWN_ON_OTHER_REFUSAL = (label: string): string =>
  `"Amount not known" applies to sold and deposit only, and this outcome is ${label}.`;

/**
 * The one decision `recordOutcome` and every surface apply before a write: the amount, its status and its
 * band, or the sentence that says why not. The band is computed here from the amount, never taken from a caller.
 */
export function resolveOutcomeMoney(input: {
  readonly label: string;
  readonly amountCents?: number | undefined;
  readonly amountUnknown?: boolean | undefined;
}): OutcomeMoney {
  const carriesAmount = (OUTCOME_AMOUNT_LABELS as readonly string[]).includes(input.label);
  const hasAmount = input.amountCents !== undefined;
  const unknown = input.amountUnknown === true;

  if (!carriesAmount) {
    if (hasAmount) return { ok: false, refusal: outcomeAmountRefusal(input.label, input.amountCents as number) ?? "An amount is not recorded on this outcome." };
    if (unknown) return { ok: false, refusal: UNKNOWN_ON_OTHER_REFUSAL(input.label) };
    return { ok: true, amountCents: null, amountStatus: null, priceBand: null };
  }
  if (hasAmount && unknown) return { ok: false, refusal: BOTH_REFUSAL };
  if (!hasAmount && !unknown) return { ok: false, refusal: NEITHER_REFUSAL(input.label) };
  if (unknown) return { ok: true, amountCents: null, amountStatus: "unknown", priceBand: null };

  const cents = input.amountCents as number;
  const refusal = outcomeAmountRefusal(input.label, cents);
  if (refusal !== null) return { ok: false, refusal };
  return { ok: true, amountCents: cents, amountStatus: "stated", priceBand: priceBandOf(cents) };
}

export interface RevenueRow {
  readonly outcomeId: string;
  readonly threadId: string;
  readonly label: string;
  readonly amountCents: number | null;
  /** Null on a label that carries no amount, and on a legacy row that predates migration 0060. */
  readonly amountStatus: OutcomeAmountStatus | null;
  readonly recordedAt: Date;
  /** Any reversal (the stamp or an outcome.reversed event) already resolved by the loader. */
  readonly reversedAt: Date | null;
}

export interface RevenueLine {
  readonly cents: number;
  /** Threads whose amount is stated and counted in `cents`. */
  readonly count: number;
  /** Threads whose amount is not known: counted apart, never as zero. */
  readonly amountUnknown: number;
}

export interface RealizedRevenue {
  readonly sale: RevenueLine;
  /** Deposit revenue of threads with NO standing sale, separately labelled from sales. */
  readonly deposit: RevenueLine;
  /** Deposits not added on top because the same thread has a standing sale. */
  readonly depositsAbsorbed: number;
  /** Standing rows beyond the one counted per thread and label, surfaced so the reading is never silent. */
  readonly extraRows: number;
}

const latest = (rows: readonly RevenueRow[]): RevenueRow => {
  let best = rows[0]!;
  for (const r of rows) {
    const d = r.recordedAt.getTime() - best.recordedAt.getTime();
    if (d > 0 || (d === 0 && r.outcomeId > best.outcomeId)) best = r;
  }
  return best;
};

/**
 * A thread's standing outcomes into realized revenue. A sale counts once, at its own amount; a deposit followed
 * by a sale is not added on top (the sale is the whole price), and a deposit alone counts as deposit revenue.
 * A reversed row is excluded. Unknown amounts are a separate count. A deposit's figure is never borrowed as a
 * sale's: an unknown sale stays unknown.
 *
 * Assumption (Kabir's to rule): two standing rows of one label on one thread (a re-tap) count once, the latest;
 * the surplus is reported in `extraRows`. A legacy sold/deposit row with no status is read as unknown.
 */
export function realizedRevenue(rows: readonly RevenueRow[]): RealizedRevenue {
  const byThread = new Map<string, RevenueRow[]>();
  for (const r of rows) {
    if (r.reversedAt !== null) continue;
    if (r.label !== "sold" && r.label !== "deposit") continue;
    if (r.amountStatus === "stated" && (r.amountCents === null || !Number.isSafeInteger(r.amountCents) || r.amountCents <= 0)) {
      throw new Error(`outcome ${r.outcomeId} is stated but has no whole-cent amount above zero.`);
    }
    if (r.amountStatus === "unknown" && r.amountCents !== null) {
      throw new Error(`outcome ${r.outcomeId} has an unknown amount status but carries an amount.`);
    }
    const list = byThread.get(r.threadId) ?? [];
    list.push(r);
    byThread.set(r.threadId, list);
  }

  const sale = { cents: 0, count: 0, amountUnknown: 0 };
  const deposit = { cents: 0, count: 0, amountUnknown: 0 };
  let depositsAbsorbed = 0;
  let extraRows = 0;

  const add = (line: { cents: number; count: number; amountUnknown: number }, r: RevenueRow): void => {
    if (r.amountStatus === "stated" && r.amountCents !== null) {
      line.cents += r.amountCents;
      line.count += 1;
    } else {
      // unknown, or a legacy row with no status: never zero.
      line.amountUnknown += 1;
    }
  };

  for (const list of byThread.values()) {
    const solds = list.filter((r) => r.label === "sold");
    const deposits = list.filter((r) => r.label === "deposit");
    if (solds.length > 0) {
      extraRows += solds.length - 1;
      add(sale, latest(solds));
      depositsAbsorbed += deposits.length;
    } else if (deposits.length > 0) {
      extraRows += deposits.length - 1;
      add(deposit, latest(deposits));
    }
  }
  return { sale, deposit, depositsAbsorbed, extraRows };
}
